/**
 * Contatos (DEMO-03, telas/Contatos.tsx): visão CONSOLIDADA dos contatos
 * cadastrados nos Clientes (matrizes e filiais). Não é base duplicada — editar
 * aqui altera o contato no Cliente. Persistente: busca, filtros e ordenação
 * vêm da API; cada edição é auditada.
 * Exportação CSV para e-mail marketing: aguarda o cabeçalho definitivo
 * (DEMO-03-pendencias §3) — não disponível nesta etapa.
 */
import { useEffect, useState } from 'react';
import { useRotuloTrilha } from '../componentes/trilha.tsx';

import { AtividadesDoContato } from './Atividades.tsx';
import { api, consulta } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Ajuda, Modal, SeloSituacao, CampoBusca, EstadoCarga, Th, useCarga, useOrdenacao } from './ui.tsx';
import { BarraFiltros, filtroSelecao } from './filtros.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { Icone } from '../componentes/icones.tsx';
import { ROTULO_TIPO_ENTIDADE, rotuloStatus, type ClienteResumo, type Contato, type ContatoConsolidado, type ContatoDetalhe as TContatoDetalhe, type Entidade, type Opcoes } from './tipos.ts';

type ColContato = 'nome' | 'empresa' | 'entidade' | 'cargo' | 'departamento' | 'papel' | 'principal' | 'situacao';
interface RespostaContatos {
  contatos: ContatoConsolidado[];
  filtros: { clientes: { id: string; nome: string }[]; segmentos: string[]; subsegmentos: string[] };
}

export function Contatos() {
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
    const [segmento, setSegmento] = useState('');
  const [subsegmento, setSubsegmento] = useState('');
  const [situacao, setSituacao] = useState<'' | 'ativo' | 'inativo'>('');
  const [editar, setEditar] = useState<Contato | null>(null);
  const [novo, setNovo] = useState(false);
  const ord = useOrdenacao<ColContato>('nome');
  // P2-03/P2-04 (D7)
  const pers = usePersonalizacao('contatos', { busca: { valor: busca, definir: setBusca },
    filtros: { segmento, subsegmento, situacao },
    aplicarFiltros: (e: Record<string, unknown>) => (setSegmento((e.segmento as string) ?? ''), setSubsegmento((e.subsegmento as string) ?? ''), setSituacao((e.situacao as '' | 'ativo' | 'inativo') ?? '')),
    ordenacao: ord,
  });
  const carga = useCarga(
    () => api.get<RespostaContatos>(`/api/contatos${consulta({ busca: busca.trim(), segmento, subsegmento, status: situacao })}`),
    [busca, segmento, subsegmento, situacao],
  );
  const f = carga.dados?.filtros;
  // F1-07: ordenação pelo cabeçalho (sem "Ordenar por")
  const lista = ord.ordenar(carga.dados?.contatos ?? [], {
    nome: (k) => k.nome,
    empresa: (k) => k.cliente.nome,
    entidade: (k) => `${ROTULO_TIPO_ENTIDADE[k.entidade.tipo]} ${k.entidade.nome_fantasia || k.entidade.razao_social}`,
    cargo: (k) => k.cargo,
    departamento: (k) => k.departamento,
    papel: (k) => k.papel,
    principal: (k) => (k.principal ? 0 : 1),
    situacao: (k) => k.status,
  });
  return (
    <>
      {/* F1-46: a trilha é o título; linha do topo = contagem · Novo contato (F1-48) */}
      <div className="titulo-pag-linha">
        <span className="linha-totais" data-teste="resumo-contatos">
          <b>{carga.dados ? lista.length : '…'}</b> contato(s)
        </span>
        <Ajuda titulo="Base única de contatos">Visão consolidada dos contatos dos Clientes, matrizes e filiais — uma única base. Alterar um contato aqui altera o Cliente, e vice-versa.</Ajuda>
        <span className="espaco" />
        {pode('criar', 'contato') && (
          <button type="button" className="botao-primario" onClick={() => setNovo(true)} data-teste="novo-contato-lista">
            <Icone nome="mais" /> Novo contato
          </button>
        )}
      </div>
      <BarraFiltros
        teste="contatos"
        direita={pers.controles}
        fixos={
          <>
            {/* P2-08 (D8): a busca ocupa o lugar do seletor Empresa e encontra pelo contato e pela empresa */}
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar contato" ajuda="Contato, empresa, CNPJ ou e-mail" dica="Nome do contato, empresa (Grupo), razão social, CNPJ (com ou sem máscara), e-mail ou telefone" teste="busca-contatos" total={busca.trim() ? lista.length : null} />
            <label>
              Status
              <select value={situacao} onChange={(e) => setSituacao(e.target.value as '' | 'ativo' | 'inativo')} data-teste="filtro-situacao-contato">
                <option value="">Todas</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </label>
          </>
        }
        opcionais={[
          filtroSelecao('segmento', 'Segmento', segmento, (v) => (setSegmento(v), setSubsegmento('')), (f?.segmentos ?? []).map((x) => ({ id: x, nome: x })), 'filtro-segmento'),
          filtroSelecao('subsegmento', 'Subsegmento', subsegmento, setSubsegmento, (f?.subsegmentos ?? []).map((x) => ({ id: x, nome: x })), 'filtro-subsegmento'),
        ]}
        aoLimpar={() => (setSegmento(''), setSubsegmento(''), setSituacao(''), setBusca(''))}
      />
      <EstadoCarga carregando={carga.carregando && !carga.dados} erro={carga.erro} recarregar={carga.recarregar} teste="contatos" />
      {carga.dados &&
        (lista.length === 0 ? (
          <p className="vazio" data-teste="contatos-vazio">
            {busca.trim() || segmento || subsegmento || situacao ? 'Nenhum contato com estes filtros.' : 'Nenhum contato cadastrado.'}
          </p>
        ) : (
          <div className="rolagem quadro-tabela">
            <table ref={pers.tabela} className="tabela tabela-contatos" data-teste="lista-contatos">
              <thead>
                <tr>
                  <Th ord={ord} coluna="nome" className="col-fixa">
                    Nome
                  </Th>
                  <Th ord={ord} coluna="empresa">Empresa/Grupo</Th>
                  <Th ord={ord} coluna="entidade">Matriz/Filial</Th>
                  <Th ord={ord} coluna="cargo">Cargo</Th>
                  <Th ord={ord} coluna="departamento">Departamento</Th>
                  <Th>E-mail</Th>
                  <Th>Telefone / WhatsApp</Th>
                  <Th ord={ord} coluna="papel">Papel na negociação</Th>
                  <Th ord={ord} coluna="principal" className="centro">
                    Principal
                  </Th>
                  <Th ord={ord} coluna="situacao">Status</Th>
                  <Th numero>Ações</Th>
                </tr>
              </thead>
              <tbody>
                {lista.map((k) => (
                  <tr key={k.id}>
                    <td className="col-fixa">
                      <a href={`#/contatos/${k.id}`}>{k.nome}</a>
                    </td>
                    <td>
                      <a href={`#/clientes/${k.cliente.id}`}>{k.cliente.nome}</a>
                    </td>
                    <td>
                      {ROTULO_TIPO_ENTIDADE[k.entidade.tipo]}
                      <div className="texto-secundario nowrap">{k.entidade.cnpj}</div>
                    </td>
                    <td>{k.cargo || <span className="ausente">—</span>}</td>
                    <td>{k.departamento || <span className="ausente">—</span>}</td>
                    <td>{k.email || <span className="ausente">sem e-mail</span>}</td>
                    <td className="nowrap">
                      {k.telefone || (!k.whatsapp && <span className="ausente">—</span>)}
                      {k.whatsapp && <div className={k.telefone ? 'texto-secundario' : undefined}>{k.whatsapp}</div>}
                    </td>
                    <td>{k.papel || <span className="ausente">—</span>}</td>
                    <td className="centro">{k.principal ? <span title="Contato principal do Cliente">★ principal</span> : ''}</td>
                    <td>
                      <SeloSituacao texto={rotuloStatus(k.status)} tom={k.status === 'ativo' ? 'positivo' : 'encerrado'} />
                    </td>
                    <td className="numero">
                      {pode('editar', 'contato') && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEditar(k)}>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rodape-tabela">{lista.length} contato(s)</div>
          </div>
        ))}
      {editar && <ModalContato contato={editar} clienteId={editar.cliente_id} aoFechar={() => setEditar(null)} aoSalvar={() => (setEditar(null), void carga.recarregar())} />}
      {novo && <ModalContato aoFechar={() => setNovo(false)} aoSalvar={() => (setNovo(false), void carga.recarregar())} />}
    </>
  );
}

/** Papel na negociação (C17/F1-49): lista do parâmetro; o valor já gravado fora da lista é preservado e mostrado como tal. */
export function CampoPapel({ valor, aoMudar, papeis, configurados, teste }: { valor: string; aoMudar: (v: string) => void; papeis: string[]; configurados: boolean; teste?: string }) {
  if (!configurados)
    return (
      <label>
        Papel na negociação
        <input value={valor} onChange={(e) => aoMudar(e.target.value)} data-teste={teste} />
      </label>
    );
  return (
    <label>
      Papel na negociação
      <select value={valor} onChange={(e) => aoMudar(e.target.value)} data-teste={teste}>
        <option value="">Selecione…</option>
        {valor && !papeis.includes(valor) && <option value={valor}>{valor} (valor anterior)</option>}
        {papeis.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Edição do contato (ou inclusão, sem `contato`): altera a fonte única (o Cliente).
 * Um só contato principal por grupo: marcar este desmarca o anterior.
 */
export function ModalContato({ contato, clienteId: clienteFixo, entidades: entidadesDadas, aoFechar, aoSalvar }: { contato?: Contato; clienteId?: string; entidades?: Entidade[]; aoFechar: () => void; aoSalvar: () => void }) {
  // F1-48: pela listagem geral, o Cliente e a entidade são escolhidos aqui (obrigatórios)
  const [clienteEscolhido, setClienteEscolhido] = useState('');
  const clienteId = clienteFixo ?? clienteEscolhido;
  const clientes = useCarga(async () => (clienteFixo ? [] : (await api.get<ClienteResumo[]>('/api/clientes')).filter((c) => c.status === 'ativo')), [clienteFixo]);
  const opcoes = useCarga(() => api.get<Opcoes>('/api/clientes/opcoes'), []);
  const [k, setK] = useState<Partial<Contato>>(() => contato ?? { nome: '', principal: false, status: 'ativo' });
  const [erro, setErro] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [salvando, setSalvando] = useState(false);
  const migrado = Boolean(contato?.origem_sistema);
  const ents = useCarga(async () => entidadesDadas ?? (clienteId ? (await api.get<{ entidades: Entidade[] }>(`/api/clientes/${clienteId}`)).entidades.filter((e) => e.status === 'ativo' || e.id === contato?.entidade_id) : []), [clienteId]);
  useEffect(() => setK((x) => (clienteFixo ? x : { ...x, entidade_id: undefined })), [clienteEscolhido, clienteFixo]);
  const entidades = ents.dados ?? [];
  const entidadeId = k.entidade_id ?? entidades[0]?.id ?? '';
  const campo = (rotulo: string, chave: keyof Contato, teste?: string) => (
    <label key={chave}>
      {rotulo}
      <input value={String(k[chave] ?? '')} onChange={(e) => setK({ ...k, [chave]: e.target.value })} data-teste={teste} />
    </label>
  );
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    const corpo = { nome: k.nome ?? '', cargo: k.cargo ?? '', departamento: k.departamento ?? '', email: k.email ?? '', telefone: k.telefone ?? '', whatsapp: k.whatsapp ?? '', papel: k.papel ?? '', observacao: k.observacao ?? '', principal: Boolean(k.principal), status: k.status ?? 'ativo', entidade_id: entidadeId };
    try {
      if (contato) await api.patch(`/api/contatos/${contato.id}`, { ...corpo, justificativa: justificativa.trim() || undefined, atualizado_em: contato.atualizado_em });
      else await api.post(`/api/clientes/${clienteId}/contatos`, corpo);
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={contato ? `Editar contato — ${contato.nome}` : 'Novo contato'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={salvando || !clienteId || !entidadeId || !(k.nome ?? '').trim() || (migrado && !justificativa.trim())} data-teste="salvar-contato">
            Salvar contato
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-contato">
          {erro}
        </p>
      )}
      {!clienteFixo && (
        <div className="form-grade grade-2">
          <label>
            Cliente *
            <select value={clienteEscolhido} onChange={(e) => setClienteEscolhido(e.target.value)} data-teste="contato-cliente">
              <option value="">{clientes.dados ? 'Escolha o Cliente' : 'Carregando…'}</option>
              {(clientes.dados ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
          <span />
        </div>
      )}
      <div className="grade-form sem-borda">
        {campo('Nome *', 'nome', 'contato-nome')}
        {campo('Cargo', 'cargo', 'contato-cargo')}
        {campo('Departamento', 'departamento')}
        {campo('E-mail', 'email', 'contato-email')}
        {campo('Telefone com DDD', 'telefone')}
        {campo('WhatsApp', 'whatsapp')}
        <CampoPapel valor={k.papel ?? ''} aoMudar={(v) => setK({ ...k, papel: v })} papeis={opcoes.dados?.papeis_contato ?? []} configurados={Boolean(opcoes.dados?.papeis_configurados)} teste="contato-papel" />
        <label>
          Matriz/filial relacionada *
          <select value={entidadeId} onChange={(e) => setK({ ...k, entidade_id: e.target.value })} disabled={!entidades.length} data-teste="contato-entidade">
            {!entidades.length && <option value="">{clienteId ? 'Carregando…' : 'Escolha antes o Cliente'}</option>}
            {entidades.map((e) => (
              <option key={e.id} value={e.id}>
                {ROTULO_TIPO_ENTIDADE[e.tipo]}: {e.nome_fantasia || e.razao_social} — {e.cnpj}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={k.status ?? 'ativo'} onChange={(e) => setK({ ...k, status: e.target.value as Contato['status'] })} data-teste="contato-situacao">
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </label>
        <label className="opcao">
          <input type="checkbox" checked={Boolean(k.principal)} onChange={(e) => setK({ ...k, principal: e.target.checked })} data-teste="contato-principal" /> Contato principal
        </label>
        <label className="largura-total">
          Observação
          <input value={k.observacao ?? ''} onChange={(e) => setK({ ...k, observacao: e.target.value })} />
        </label>
        {migrado && (
          <label className="largura-total">
            Justificativa (obrigatória: registro migrado da planilha)
            <textarea rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={1000} data-teste="justificativa-contato" />
          </label>
        )}
      </div>
    </Modal>
  );
}

/** Cadastro do contato (aberto a partir das entidades do Cliente e da lista de Contatos). */
export function ContatoDetalhe({ id }: { id: string }) {
  const { pode } = useSessao();
  const [editar, setEditar] = useState(false);
  const carga = useCarga(() => api.get<TContatoDetalhe>(`/api/contatos/${id}`), [id]);
  useRotuloTrilha(carga.dados?.nome);
  const k = carga.dados;
  if (!k) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="contato" />;
  const e = k.entidade;
  return (
    <>
      <section className="resumo-destaque" data-teste="cadastro-contato">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">{k.nome}</h1>
          <div className="selos-cabecalho">
            {k.principal && <span className="selo selo-destaque">★ Contato principal</span>}
            <SeloSituacao texto={rotuloStatus(k.status)} tom={k.status === 'ativo' ? 'positivo' : 'encerrado'} />
            {pode('editar', 'contato') && (
              <div className="acoes-direita">
                <button type="button" className="botao-primario" onClick={() => setEditar(true)} data-teste="editar-contato">
                  Editar contato
                </button>
              </div>
            )}
          </div>
        </div>
        <dl className="resumo-campos">
          <div>
            <dt>Cliente</dt>
            <dd>
              <a href={`#/clientes/${k.cliente.id}`}>{k.cliente.nome}</a>
            </dd>
          </div>
          <div>
            <dt>Matriz/filial</dt>
            <dd>
              {ROTULO_TIPO_ENTIDADE[e.tipo]}: {e.razao_social} — <span className="nowrap">{e.cnpj}</span>
            </dd>
          </div>
          <div>
            <dt>Cargo · departamento</dt>
            <dd>{[k.cargo, k.departamento].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div>
            <dt>Papel na negociação</dt>
            <dd>{k.papel || '—'}</dd>
          </div>
          <div>
            <dt>E-mail</dt>
            <dd>{k.email || 'sem e-mail'}</dd>
          </div>
          <div>
            <dt>Telefone · WhatsApp</dt>
            <dd>{[k.telefone, k.whatsapp].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div>
            <dt>Observação</dt>
            <dd>{k.observacao || '—'}</dd>
          </div>
        </dl>
      </section>
      <h2>Atividades com este contato</h2>
      {pode('visualizar', 'atividade') ? <AtividadesDoContato contatoId={k.id} /> : <p className="texto-secundario">Sem permissão para ver atividades.</p>}
      {editar && <ModalContato contato={k} clienteId={k.cliente_id} aoFechar={() => setEditar(false)} aoSalvar={() => (setEditar(false), void carga.recarregar())} />}
    </>
  );
}
